import { useTranslation } from 'react-i18next'
import { Section } from '#app/ui/components/custom/section'
import { SectionHeader } from '#app/ui/components/custom/section-header'

type CompoundStep = { step?: string; title: string; body: string }

type ArrowDirection = 'up' | 'down' | 'left' | 'right'

/** One arrow: head at the far end, tail at the near end. */
const LongArrow = ({
	direction,
	className,
}: {
	direction: ArrowDirection
	className?: string
}) => {
	const vertical = direction === 'up' || direction === 'down'
	const path =
		direction === 'up'
			? 'M6 0 12 9H7.5v91h-3V9H0Z'
			: direction === 'down'
				? 'M6 100 0 91h4.5V0h3v91H12Z'
				: direction === 'left'
					? 'M0 6 9 0v4.5H100v3H9V12Z'
					: 'M100 6 91 12V7.5H0v-3h91V0Z'

	return (
		<svg
			aria-hidden
			className={className}
			fill="currentColor"
			preserveAspectRatio="none"
			viewBox={vertical ? '0 0 12 100' : '0 0 100 12'}
		>
			<path d={path} />
		</svg>
	)
}

/**
 * Hourglass bands after Durabl v6 "Why it matters": teal widens,
 * grey thins, waist on one y so station rules can run through.
 */
const Bands = () => (
	<svg
		aria-hidden
		className="absolute inset-0 size-full"
		preserveAspectRatio="none"
		viewBox="0 0 1000 320"
	>
		<defs>
			<linearGradient id="compound-pipe-fill" x1="0" x2="1" y1="0" y2="0">
				<stop offset="0%" stopColor="rgb(15 118 110)" stopOpacity="0.2" />
				<stop offset="35%" stopColor="rgb(13 148 136)" stopOpacity="0.45" />
				<stop offset="70%" stopColor="rgb(20 184 166)" stopOpacity="0.7" />
				<stop offset="100%" stopColor="rgb(94 234 212)" stopOpacity="0.9" />
			</linearGradient>
			<linearGradient id="compound-usual-fill" x1="0" x2="1" y1="0" y2="0">
				<stop offset="0%" stopColor="rgb(113 113 122)" stopOpacity="0.12" />
				<stop offset="50%" stopColor="rgb(82 82 91)" stopOpacity="0.28" />
				<stop offset="100%" stopColor="rgb(63 63 70)" stopOpacity="0.5" />
			</linearGradient>
		</defs>
		<path
			d="M0 160 L0 142 C120 140 180 128 280 108 C420 80 520 52 660 36 C800 20 900 16 1000 14 L1000 160 Z"
			fill="url(#compound-pipe-fill)"
		/>
		<path
			d="M0 160 L0 178 C120 180 180 192 280 212 C420 240 520 268 660 284 C800 300 900 304 1000 306 L1000 160 Z"
			fill="url(#compound-usual-fill)"
		/>
	</svg>
)

/** Desktop graph stood on end: time runs down, pipe to the right, usual way to the left. */
const VerticalBands = () => (
	<svg
		aria-hidden
		className="absolute inset-0 size-full"
		preserveAspectRatio="none"
		viewBox="0 0 80 500"
	>
		<defs>
			<linearGradient id="compound-v-pipe-fill" x1="0" x2="0" y1="0" y2="1">
				<stop offset="0%" stopColor="rgb(15 118 110)" stopOpacity="0.2" />
				<stop offset="35%" stopColor="rgb(13 148 136)" stopOpacity="0.45" />
				<stop offset="70%" stopColor="rgb(20 184 166)" stopOpacity="0.7" />
				<stop offset="100%" stopColor="rgb(94 234 212)" stopOpacity="0.9" />
			</linearGradient>
			<linearGradient id="compound-v-usual-fill" x1="0" x2="0" y1="0" y2="1">
				<stop offset="0%" stopColor="rgb(113 113 122)" stopOpacity="0.12" />
				<stop offset="50%" stopColor="rgb(82 82 91)" stopOpacity="0.28" />
				<stop offset="100%" stopColor="rgb(63 63 70)" stopOpacity="0.5" />
			</linearGradient>
		</defs>
		<path
			d="M40 0 L43 0 C48 120 56 240 66 360 C72 430 75 470 76 500 L40 500 Z"
			fill="url(#compound-v-pipe-fill)"
		/>
		<path
			d="M40 0 L37 0 C32 120 24 240 14 360 C8 430 5 470 4 500 L40 500 Z"
			fill="url(#compound-v-usual-fill)"
		/>
	</svg>
)

export const Compound = () => {
	const { t } = useTranslation('home')
	const steps = t('services.compound.steps', {
		returnObjects: true,
	}) as CompoundStep[]
	const usual = t('services.compound.usual.steps', {
		returnObjects: true,
	}) as CompoundStep[]

	return (
		<Section tone="panel">
			<SectionHeader
				eyebrow={t('services.compound.eyebrow')}
				heading={t('services.compound.heading')}
				lead={t('services.compound.lead')}
			/>

			{/*
			  One diagram, like Durabl: captions sit on the curves,
			  one rule per station from top caption through both bands
			  to the caption below.
			*/}
			<div className="relative mt-fluid-16 hidden lg:block">
				<div className="grid grid-cols-5">
					{steps.map((step, index) => (
						<div
							key={step.title}
							className="flex flex-col border-l border-white/15 first:border-l-0"
						>
							<div className="relative h-64">
								<div
									className="absolute inset-x-0 px-3"
									style={{ top: `${8 + (4 - index) * 36}px` }}
								>
									<p className="text-sm font-medium leading-snug text-white">
										{step.title}
									</p>
									<p className="mt-1 text-sm leading-snug text-white/55">
										{step.body}
									</p>
								</div>
							</div>
							<div className="h-72 shrink-0" />
							<div
								className="px-3"
								style={{ paddingTop: `${12 + index * 36}px` }}
							>
								<p className="text-sm font-medium leading-snug text-white/80">
									{usual[index]?.title}
								</p>
								<p className="mt-1 text-sm leading-snug text-white/45">
									{usual[index]?.body}
								</p>
							</div>
						</div>
					))}
				</div>

				<div className="pointer-events-none absolute inset-x-0 top-64 h-72">
					<div className="absolute inset-y-0 left-0 right-28">
						<Bands />
					</div>
					<p className="absolute top-[28%] left-3 text-xs font-medium text-white/70">
						{t('services.compound.pipeLabel')}
					</p>
					<p className="absolute bottom-[22%] left-3 text-xs font-medium text-white/45">
						{t('services.compound.usual.label')}
					</p>
					<div className="absolute inset-y-0 right-0 flex w-28 flex-col">
						<div className="flex h-1/2 flex-col px-3 pt-4">
							<p className="shrink-0 text-xs font-medium leading-snug tracking-wide text-white">
								{t('services.compound.pipeEnd')}
							</p>
							<LongArrow
								direction="up"
								className="mx-auto mt-2 min-h-0 w-3 flex-1 text-accent-dot"
							/>
						</div>
						<div className="flex h-1/2 flex-col px-3 pb-4">
							<LongArrow
								direction="down"
								className="mx-auto mb-2 min-h-0 w-3 flex-1 text-zinc-400"
							/>
							<p className="shrink-0 text-xs font-medium leading-snug tracking-wide text-zinc-300">
								{t('services.compound.usual.end')}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/*
			  Mobile, after Durabl v7: the graph stands up. Time runs
			  down. Usual way on the left, pipe on the right.
			*/}
			<div className="mt-fluid-16 lg:hidden">
				<div className="relative">
					<div className="pointer-events-none absolute inset-y-2 left-1/2 w-28 -translate-x-1/2">
						<VerticalBands />
					</div>
					<ol>
						{steps.map((step, index) => (
							<li
								key={step.title}
								className="grid grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)] items-start gap-1 border-t border-white/10 py-6 first:border-t-0"
							>
								<div className="text-right">
									<p className="text-sm font-medium leading-snug text-white/80">
										{usual[index]?.title}
									</p>
									<p className="mt-1 text-xs leading-snug text-white/45">
										{usual[index]?.body}
									</p>
								</div>
								<div />
								<div>
									<p className="text-sm font-medium leading-snug text-white">
										{step.title}
									</p>
									<p className="mt-1 text-xs leading-snug text-white/55">
										{step.body}
									</p>
								</div>
							</li>
						))}
					</ol>
				</div>
				<div className="mt-6 grid grid-cols-2 items-center px-1">
					<div className="flex min-w-0 items-center gap-2">
						<p className="shrink-0 text-xs font-medium tracking-wide text-zinc-300">
							{t('services.compound.usual.end')}
						</p>
						<LongArrow
							direction="left"
							className="h-3 min-w-0 flex-1 text-zinc-400"
						/>
					</div>
					<div className="flex min-w-0 items-center gap-2">
						<LongArrow
							direction="right"
							className="h-3 min-w-0 flex-1 text-accent-dot"
						/>
						<p className="shrink-0 text-xs font-medium tracking-wide text-white">
							{t('services.compound.pipeEnd')}
						</p>
					</div>
				</div>
			</div>
		</Section>
	)
}
